import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Typography, Flex, Segmented, message } from "antd";
import loginImg from "../../assets/images/login-img.svg";
import { Logo } from "../../assets/CustomIcon";
import CaptchaWidget from "../../components/Auth/CaptchaWidget";
import { config } from "../../config";

import "../../assets/styles/component_styles/validation.css";
import "../../assets/styles/component_styles/login.css";
import { hashPassword, isStrongPassword as checkStrongPassword, isValidEmail, isValidPhone } from "../../utils/appUtils";
import { VALIDATION_MESSAGES } from "../../utils/constant";
import {
    forgotPasswordUser,
    selectForgotPasswordSuccess,
    selectForgotPasswordLoading,
    selectForgotPasswordError,
    resetForgotPasswordSuccess,
} from "../../redux/userSlice";

const { Title, Text } = Typography;

export default function ForgotPassword() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const forgotSuccess = useSelector(selectForgotPasswordSuccess);
    const forgotLoading = useSelector(selectForgotPasswordLoading);
    const forgotError = useSelector(selectForgotPasswordError);

    useEffect(() => {
        if (forgotSuccess) {
            message.success("Password updated successfully. Please login.");
            dispatch(resetForgotPasswordSuccess());
            navigate("/");
        }
    }, [forgotSuccess, dispatch, navigate]);

    useEffect(() => {
        if (forgotError && captchaRef.current) captchaRef.current.reset();
    }, [forgotError]);
    const [errors, setErrors] = useState({});
    const [resetType, setResetType] = useState("email");
    const [formData, setFormData] = useState({
        value: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef(null);
    const requireCaptcha = !!config.turnstileSiteKey;

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (errors[e.target.name]) {
            setErrors({
                ...errors,
                [e.target.name]: ""
            });
        }
    };

    const handleSubmit = async () => {
        if (!formData.value) {
            const msg = resetType === "email" ? VALIDATION_MESSAGES.EMAIL_INVALID : VALIDATION_MESSAGES.PHONE_REQUIRED;
            setErrors({ value: msg });
            return;
        }
        if (resetType === "email" && !isValidEmail(formData.value)) {
            setErrors({ value: VALIDATION_MESSAGES.EMAIL_INVALID });
            return;
        }
        if (resetType === "phone" && !isValidPhone(formData.value)) {
            setErrors({ value: VALIDATION_MESSAGES.PHONE_INVALID });
            return;
        }
        if (!formData.newPassword) {
            setErrors({ newPassword: VALIDATION_MESSAGES.PASSWORD_REQUIRED });
            return;
        }
        if (!checkStrongPassword(formData.newPassword)) {
            setErrors({ newPassword: VALIDATION_MESSAGES.PASSWORD_WEAK });
            return;
        }
        if (formData.newPassword !== formData.confirmPassword) {
            setErrors({ confirmPassword: VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH });
            return;
        }
        if (requireCaptcha && !captchaToken) {
            message.warning("Please complete the verification.");
            return;
        }

        setErrors({});

        try {
            const hashedPassword = await hashPassword(formData.newPassword);
            const payload = {
                type: resetType === "phone" ? "mobile" : "email",
                value: formData.value,
                password: hashedPassword,
            };
            if (requireCaptcha) payload.captchaToken = captchaToken;

            dispatch(forgotPasswordUser(payload));
        } catch (error) {
            console.error("Hashing failed", error);
            message.error("Something went wrong.");
            if (requireCaptcha && captchaRef.current) captchaRef.current.reset();
        }
    };

    return (
        <div className="loginPageRoot">
            <header className="topNavbar">
                <div className="container">
                    <nav className="authNavbarWrap">
                        <Title level={1} style={{ margin: 0 }} title="Pingvite">
                            <Link to="/" className="siteLogo">
                                <Logo />
                            </Link>
                        </Title>
                        <Button
                            className="menuMobileButton"
                            type="primary"
                        >
                            <span className="material-symbols-outlined">menu</span>
                        </Button>
                    </nav>
                </div>
            </header>
            {/* Main content */}
            <main className="loginMain container">
                <div className="loginImage">
                    <img src={loginImg} alt="Forgot Password" className="sideImage" />
                </div>

                {/* right form */}
                <div className="loginFormWrap">
                    <div className="loginCard">
                        <div className="loginCardInner">
                            <div className="loginCardHeader">
                                <Title level={3} className="title">
                                    Reset Password
                                </Title>
                                <Text className="color-neutral">
                                    Enter your email or phone and new password.
                                </Text>
                            </div>

                            <div className="loginTypeToggle" style={{ marginBottom: 16 }}>
                                <Segmented
                                    options={[
                                        { label: "Email", value: "email" },
                                        { label: "Phone", value: "phone" },
                                    ]}
                                    value={resetType}
                                    onChange={(v) => {
                                        setResetType(v);
                                        setFormData({ ...formData, value: "" });
                                        setErrors({});
                                    }}
                                    block
                                    size="large"
                                />
                            </div>

                            <Form name="forgotPassword" layout="vertical" onFinish={handleSubmit}>
                                <Form.Item
                                    required
                                    label={resetType === "email" ? "Email ID" : "Phone Number"}
                                    validateStatus={errors.value ? "error" : ""}
                                    help={errors.value}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input
                                        name="value"
                                        value={formData.value}
                                        onChange={handleChange}
                                        prefix={
                                            <span className="material-symbols-outlined prefix-blue">
                                                {resetType === "email" ? "mail" : "phone"}
                                            </span>
                                        }
                                        placeholder={resetType === "email" ? "you@example.com" : "10-digit phone"}
                                        size="large"
                                        className="hasPrefixIconInput"
                                        maxLength={resetType === "phone" ? 10 : undefined}
                                    />
                                </Form.Item>

                                <Form.Item
                                    required
                                    label="New Password"
                                    validateStatus={errors.newPassword ? "error" : ""}
                                    help={errors.newPassword}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input.Password
                                        name="newPassword"
                                        value={formData.newPassword}
                                        onChange={handleChange}
                                        prefix={
                                            <span className="material-symbols-outlined prefix-blue">
                                                lock
                                            </span>
                                        }
                                        placeholder="New Password"
                                        size="large"
                                        className="hasPrefixIconInput"
                                    />
                                </Form.Item>

                                <Form.Item
                                    required
                                    label="Confirm Password"
                                    validateStatus={errors.confirmPassword ? "error" : ""}
                                    help={errors.confirmPassword}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input.Password
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        prefix={
                                            <span className="material-symbols-outlined prefix-blue">
                                                lock
                                            </span>
                                        }
                                        placeholder="Confirm Password"
                                        size="large"
                                        className="hasPrefixIconInput"
                                    />
                                </Form.Item>

                                <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />

                                <Form.Item style={{ marginTop: 18 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        size="large"
                                        className="btnGradient"
                                        loading={forgotLoading}
                                    >
                                        Update Password
                                    </Button>
                                </Form.Item>
                                <Flex align="center" justify="center">
                                    <Link to="/">
                                        <Button type="link">Back to Login</Button>
                                    </Link>
                                </Flex>
                            </Form>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
